import { NewbornPostpartumPhysicalExamTypes } from "@/fhir/enums/newborn-postpartum-physical-exams";
import { PhysicalExamTypes } from "@/fhir/enums/physical-exams";
import {
  GynecologicalPhysicalExamsQuestionnaires,
  NewbornPostpartumPhysicalExamsQuestionnaires,
  PhysicalExamQuestionnaires,
  PostpartumPhysicalExamsQuestionnaires,
} from "@/fhir/enums/questionnaires";
import NewbornPostpartumPhysicalExamHead from "@/physical-exams/exams/newborn-postpartum/PhysicalExamHead";

import { PhysicalExamHEENT } from "../exams";
import GynecologicalAbdomenPhysicalExam from "../exams/gynecological/GynecologicalAbdomenPhysicalExam";
import GynecologicalNeckPhysicalExam from "../exams/gynecological/GynecologicalNeckPhysicalExam";
import GynecologicalPerineumAndExternalGenitaliaPhysicalExam from "../exams/gynecological/GynecologicalPerineumAndExternalGenitaliaPhysicalExam";
import NewbornPostpartumPhysicalExamBackAndSpine from "../exams/newborn-postpartum/PhysicalExamBackAndSpine";
import NewbornPostpartumPhysicalExamChestAndAbdomen from "../exams/newborn-postpartum/PhysicalExamChestAndAbdomen";
import NewbornPostpartumPhysicalExamEars from "../exams/newborn-postpartum/PhysicalExamEars";
import NewbornPostpartumPhysicalExamEyes from "../exams/newborn-postpartum/PhysicalExamEyes";
import NewbornPostpartumPhysicalExamHipsAndExtremities from "../exams/newborn-postpartum/PhysicalExamHipsAndExtremities";
import NewbornPostpartumPhysicalExamNeck from "../exams/newborn-postpartum/PhysicalExamNeck";
import NewbornPostpartumPhysicalExamNoseAndMouth from "../exams/newborn-postpartum/PhysicalExamNoseAndMouth";
import PhysicalExamAbdomen from "../exams/PhysicalExamAbdomen";
import PhysicalExamBack from "../exams/PhysicalExamBack";
import PhysicalExamBreast from "../exams/PhysicalExamBreast";
import PhysicalExamExtremities from "../exams/PhysicalExamExtremities";
import PhysicalExamGeneralAppearance from "../exams/PhysicalExamGeneralAppearance";
import PhysicalExamGenitourinary from "../exams/PhysicalExamGenitourinary";
import PhysicalExamHeart from "../exams/PhysicalExamHeart";
import PhysicalExamLungs from "../exams/PhysicalExamLungs";
import PhysicalExamMusculoskeletal from "../exams/PhysicalExamMusculoskeletal";
import PhysicalExamNeurologic from "../exams/PhysicalExamNeurologic";
import PhysicalExamPsychiatric from "../exams/PhysicalExamPsychiatric";
import PhysicalExamSkin from "../exams/PhysicalExamSkin";
import PostpartumAbdomenPhysicalExam from "../exams/postpartum/PostpartumAbdomenPhysicalExam";
import PostpartumNeckPhysicalExam from "../exams/postpartum/PostpartumNeckPhysicalExam";
import PostpartumPhysicalExamPerineumAndExternalGenitalia from "../exams/postpartum/PostpartumPhysicalExamPerineumAndExternalGenitalia";
import { PhysicalExamProps } from "../types/physical-exams";

/**
 * Registers each exam component by questionnaire URL, along with the `Accordion.Item` `value`
 * it passes to its own `PhysicalExamHeader`, so the read-only view can default every added
 * section to expanded. Each component registers unsaved form state via
 * `usePhysicalExamUnsavedChanges` so `useNavigationBlocker` in EncounterBase can block navigation.
 */
export interface PhysicalExamRegistration {
  Component: React.ComponentType<PhysicalExamProps>;
  accordionValue: PhysicalExamTypes | NewbornPostpartumPhysicalExamTypes;
}

export const PHYSICAL_EXAMS_BY_URL = {
  [PhysicalExamQuestionnaires.HEENTPhysicalExam]: {
    Component: PhysicalExamHEENT,
    accordionValue: PhysicalExamTypes.HEENT,
  },
  [PhysicalExamQuestionnaires.GeneralAppearancePhysicalExam]: {
    Component: PhysicalExamGeneralAppearance,
    accordionValue: PhysicalExamTypes.GeneralAppearance,
  },
  [PhysicalExamQuestionnaires.BreastPhysicalExam]: {
    Component: PhysicalExamBreast,
    accordionValue: PhysicalExamTypes.Breast,
  },
  [PhysicalExamQuestionnaires.ExtremitiesPhysicalExam]: {
    Component: PhysicalExamExtremities,
    accordionValue: PhysicalExamTypes.Extremities,
  },
  [PhysicalExamQuestionnaires.NeurologicPhysicalExam]: {
    Component: PhysicalExamNeurologic,
    accordionValue: PhysicalExamTypes.Neurologic,
  },
  [PhysicalExamQuestionnaires.HeartPhysicalExam]: {
    Component: PhysicalExamHeart,
    accordionValue: PhysicalExamTypes.Heart,
  },
  [PhysicalExamQuestionnaires.LungsPhysicalExam]: {
    Component: PhysicalExamLungs,
    accordionValue: PhysicalExamTypes.Lungs,
  },
  [PhysicalExamQuestionnaires.GenitourinaryPhysicalExam]: {
    Component: PhysicalExamGenitourinary,
    accordionValue: PhysicalExamTypes.Genitourinary,
  },
  [PhysicalExamQuestionnaires.AbdomenPhysicalExam]: {
    Component: PhysicalExamAbdomen,
    accordionValue: PhysicalExamTypes.Abdomen,
  },
  [PhysicalExamQuestionnaires.BackPhysicalExam]: {
    Component: PhysicalExamBack,
    accordionValue: PhysicalExamTypes.Back,
  },
  [PhysicalExamQuestionnaires.MusculoskeletalPhysicalExam]: {
    Component: PhysicalExamMusculoskeletal,
    accordionValue: PhysicalExamTypes.Musculoskeletal,
  },
  [PhysicalExamQuestionnaires.SkinPhysicalExam]: {
    Component: PhysicalExamSkin,
    accordionValue: PhysicalExamTypes.Skin,
  },
  [PhysicalExamQuestionnaires.PsychiatricPhysicalExam]: {
    Component: PhysicalExamPsychiatric,
    accordionValue: PhysicalExamTypes.Psychiatric,
  },
  [NewbornPostpartumPhysicalExamsQuestionnaires.Head]: {
    Component: NewbornPostpartumPhysicalExamHead,
    accordionValue: NewbornPostpartumPhysicalExamTypes.Head,
  },
  [NewbornPostpartumPhysicalExamsQuestionnaires.Eyes]: {
    Component: NewbornPostpartumPhysicalExamEyes,
    accordionValue: NewbornPostpartumPhysicalExamTypes.Eyes,
  },
  [NewbornPostpartumPhysicalExamsQuestionnaires.Ears]: {
    Component: NewbornPostpartumPhysicalExamEars,
    accordionValue: NewbornPostpartumPhysicalExamTypes.Ears,
  },
  [NewbornPostpartumPhysicalExamsQuestionnaires.Neck]: {
    Component: NewbornPostpartumPhysicalExamNeck,
    accordionValue: NewbornPostpartumPhysicalExamTypes.Neck,
  },
  [NewbornPostpartumPhysicalExamsQuestionnaires.NoseAndMouth]: {
    Component: NewbornPostpartumPhysicalExamNoseAndMouth,
    accordionValue: NewbornPostpartumPhysicalExamTypes.NoseAndMouth,
  },
  [NewbornPostpartumPhysicalExamsQuestionnaires.ChestAndAbdomen]: {
    Component: NewbornPostpartumPhysicalExamChestAndAbdomen,
    accordionValue: NewbornPostpartumPhysicalExamTypes.ChestAndAbdomen,
  },
  [PostpartumPhysicalExamsQuestionnaires.PerineumAndExternalGenitaliaPhysicalExam]: {
    Component: PostpartumPhysicalExamPerineumAndExternalGenitalia,
    accordionValue: PhysicalExamTypes.PerineumAndExternalGenitalia,
  },
  [PostpartumPhysicalExamsQuestionnaires.AbdomenPhysicalExam]: {
    Component: PostpartumAbdomenPhysicalExam,
    accordionValue: PhysicalExamTypes.PostpartumAbdomen,
  },
  [PostpartumPhysicalExamsQuestionnaires.NeckPhysicalExam]: {
    Component: PostpartumNeckPhysicalExam,
    accordionValue: PhysicalExamTypes.PostpartumNeck,
  },
  [NewbornPostpartumPhysicalExamsQuestionnaires.HipsAndExtremities]: {
    Component: NewbornPostpartumPhysicalExamHipsAndExtremities,
    accordionValue: NewbornPostpartumPhysicalExamTypes.HipsAndExtremities,
  },
  [NewbornPostpartumPhysicalExamsQuestionnaires.BackAndSpine]: {
    Component: NewbornPostpartumPhysicalExamBackAndSpine,
    accordionValue: NewbornPostpartumPhysicalExamTypes.BackAndSpine,
  },
  [GynecologicalPhysicalExamsQuestionnaires.AbdomenPhysicalExam]: {
    Component: GynecologicalAbdomenPhysicalExam,
    accordionValue: PhysicalExamTypes.GynecologicalAbdomen,
  },
  [GynecologicalPhysicalExamsQuestionnaires.NeckPhysicalExam]: {
    Component: GynecologicalNeckPhysicalExam,
    accordionValue: PhysicalExamTypes.GynecologicalNeck,
  },
  [GynecologicalPhysicalExamsQuestionnaires.PerineumAndExternalGenitaliaPhysicalExam]: {
    Component: GynecologicalPerineumAndExternalGenitaliaPhysicalExam,
    accordionValue: PhysicalExamTypes.GynecologicalPerineumAndExternalGenitalia,
  },
} satisfies Record<string, PhysicalExamRegistration>;

export type PhysicalExamQuestionnaireURL = keyof typeof PHYSICAL_EXAMS_BY_URL;
